import { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlockAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { fetchWalletInfo } from '@/shared/store/slices/walletSlice';
import LoadingButton from '@/components/ui/LoadingButton';

/**
 * 解鎖貼文確認 Modal
 *
 * isLoading：post/unlock 回應較慢，送出後在確認鍵顯示 spinner，
 * 同時鎖住關閉途徑（X／backdrop／ESC／取消），避免使用者以為當掉而重複送出扣款請求。
 */
const PostUnlockModal = ({ show, onHide, onConfirm, onError, creatorName, unlockPrice, isLoading = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // 從 Redux 取得錢包餘額（null = 還沒拿到）
    const rawBalance = useSelector(state => state.wallet.balance);
    const currentIdolId = useSelector(state => state.user.idolId);

    // Modal 打開時獲取最新錢包餘額。
    // 抓失敗要把錯誤碼交給呼叫端的 StatusModal，否則餘額永遠是 null，
    // 使用者會停在一個不會結束的 spinner 上、確認鍵永遠灰掉且沒有任何說明。
    // 這裡用 unwrap() 接「這一次 dispatch」的結果——錯誤刻意不放進 store，
    // 否則會活過這次操作、被下一個畫面誤讀成自己的錯誤。關閉 modal 由呼叫端的 onError 一併處理。
    useEffect(() => {
        if (!show || !currentIdolId) return;
        let cancelled = false;
        dispatch(fetchWalletInfo(currentIdolId))
            .unwrap()
            .catch((statusCode) => {
                // modal 已關閉就別再彈：逾時窗口長達 180 秒，使用者早就按了取消，
                // 這時才跳出來的錯誤只會讓人莫名其妙
                if (cancelled) return;
                // 非字串＝拿不到狀態碼。RTK 在 rejectWithValue(undefined) 時
                // meta.rejectedWithValue 是 !!payload = false，unwrap() 會改丟
                // serializeError 的 { message: 'Rejected' } 物件而不是 undefined；
                // 讓它流進 StatusModal 會在 getErrorMessageKey 對物件呼叫 toLowerCase 而整棵樹崩掉
                if (typeof statusCode !== 'string') {
                    console.error('Fetch wallet info failed without status code', statusCode);
                    return;
                }
                // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
                if (statusCode === 'NETWORK_ERROR') return;
                onError(statusCode);
            });
        return () => { cancelled = true; };
    }, [show, currentIdolId, dispatch, onError]);

    if (!unlockPrice) return null;

    // 餘額還沒拿到就不顯示金額、也不讓送出：使用者是看著「扣款後餘額」決定要不要付這筆，
    // 給 0 或 NaN 都會讓他在錯誤的資訊下扣款
    const isBalanceReady = rawBalance != null;
    const walletBalance = isBalanceReady ? parseFloat(rawBalance) : null;
    const afterPaymentBalance = isBalanceReady ? walletBalance - unlockPrice : null;

    // 格式化金額到小數點後最多 2 位
    const formatPrice = (price) => parseFloat(price.toFixed(2));

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop={isLoading ? 'static' : true}
            keyboard={!isLoading}
        >
            <Modal.Header closeButton={!isLoading} className="border-bottom pb-3">
                <Modal.Title className="fw-bold">{t('unlock_post')}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-4">
                {/* 創作者 */}
                <div className="mb-4">
                    <h6>{t('creator')}</h6>
                    <p className="mb-0">{creatorName}</p>
                </div>

                {/* 應付金額 */}
                <div className="mb-3">
                    <h6>{t('amount_to_pay')}</h6>
                    <p className="text-primary fw-bold mb-0">₣{unlockPrice} FCoin</p>
                </div>

                {/* 解鎖後可永久觀看 */}
                <div className="mb-3 d-flex align-items-center gap-2 text-gray">
                    <FontAwesomeIcon icon={faUnlockAlt} />
                    <span>{t('unlock_permanent_access')}</span>
                </div>

                <hr />

                {isBalanceReady ? (
                    <>
                        {/* 錢包餘額 */}
                        <div className="mb-3">
                            <h6>{t('wallet_balance')}</h6>
                            <p className="text-primary fw-bold mb-0">₣{walletBalance} FCoin</p>
                        </div>

                        {/* 扣款後餘額 */}
                        <div>
                            <h6>{t('balance_after_payment')}</h6>
                            <p className="text-primary fw-bold mb-0">₣{formatPrice(afterPaymentBalance)} FCoin</p>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-3">
                        <span className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('loading')}</span>
                        </span>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 justify-content-end gap-2">
                <button className="btn btn-neutral rounded-pill" onClick={onHide} disabled={isLoading}>
                    {t('cancel')}
                </button>
                <LoadingButton
                    className="btn btn-primary rounded-pill"
                    onClick={onConfirm}
                    isLoading={isLoading}
                    disabled={!isBalanceReady}
                >
                    {t('confirm')}
                </LoadingButton>
            </Modal.Footer>
        </Modal>
    );
};

export default PostUnlockModal;
